import React, { useState } from 'react';
import { apiClient } from '../../../utils/apiClient';

/**
 * Selector des 3 fréquences d'alerte pour un suivi de jurisprudence.
 *
 * Appelle PUT /jurisprudence-suivi/:jurisprudenceId/settings dès que
 * l'utilisateur change l'option. Pendant la requête, le selector est
 * désactivé et l'ancienne valeur reste visible — en cas d'échec, on
 * revient à l'ancienne valeur et on affiche un message d'erreur.
 *
 * Composant volontairement minimal : pas de toast global, pas
 * d'animations, juste le strict nécessaire pour rendre la fréquence
 * configurable.
 */

export type AlertFrequency = 'immediate' | 'daily' | 'weekly';

interface AlertFrequencySelectorProps {
  jurisprudenceId: string;
  value: AlertFrequency;
  onChange: (next: AlertFrequency) => void;
  disabled?: boolean;
}

const LABELS: Record<AlertFrequency, string> = {
  immediate: 'Immédiat',
  daily: 'Récap quotidien',
  weekly: 'Récap hebdomadaire',
};

const AlertFrequencySelector: React.FC<AlertFrequencySelectorProps> = ({
  jurisprudenceId,
  value,
  onChange,
  disabled,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as AlertFrequency;
    if (next === value) return;

    setSaving(true);
    setError(null);
    try {
      const response = await apiClient(`/jurisprudence-suivi/${jurisprudenceId}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ alertFrequency: next }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      onChange(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || saving}
        aria-label="Fréquence d'alerte"
        className="text-xs border rounded px-2 py-1 bg-white dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 border-gray-300 dark:border-gray-600 disabled:opacity-50"
      >
        {(['immediate', 'daily', 'weekly'] as const).map(f => (
          <option key={f} value={f}>
            {LABELS[f]}
          </option>
        ))}
      </select>
      {saving && <span className="text-xs text-gray-500">…</span>}
      {error && (
        <span className="text-xs text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default AlertFrequencySelector;
